// Trade notification store — reactive toast stack
// Manages up to MAX_TOASTS visible at once, auto-dismisses oldest

const MAX_TOASTS = 3;
const DISMISS_INTERVAL = 2000; // ms between auto-dismissals
const DEBOUNCE_TIMEOUT = 2500; // ms debounce per-card

let toasts = $state([]);
let nextId = 0;
let dismissTimer = null;

// Per-card debounce state
let pendingChange = null;
let pendingCardKey = null;
let debounceTimer = null;

// --- Toast management ---

function startDismissTimer() {
  if (dismissTimer) return;
  dismissTimer = setInterval(() => {
    // Remove the oldest non-persistent toast
    const idx = toasts.findIndex((t) => !t.persistent);
    if (idx !== -1) {
      toasts.splice(idx, 1);
    }
    if (toasts.length === 0) {
      clearInterval(dismissTimer);
      dismissTimer = null;
    }
  }, DISMISS_INTERVAL);
}

function addToast(message, options = {}) {
  const toast = {
    id: nextId++,
    message,
    type: options.type || 'info',
    subtitle: options.subtitle || null,
    persistent: options.persistent || false,
    duration: options.duration || null,
  };

  // Enforce max toasts — remove oldest first
  while (toasts.length >= MAX_TOASTS) {
    toasts.shift();
  }

  toasts.push(toast);
  startDismissTimer();

  // Auto-remove after custom duration
  if (toast.duration) {
    setTimeout(() => removeToast(toast.id), toast.duration);
  }
}

function removeToast(id) {
  const idx = toasts.findIndex((t) => t.id === id);
  if (idx !== -1) toasts.splice(idx, 1);
  if (toasts.length === 0 && dismissTimer) {
    clearInterval(dismissTimer);
    dismissTimer = null;
  }
}

function clearAll() {
  toasts.length = 0;
  if (dismissTimer) {
    clearInterval(dismissTimer);
    dismissTimer = null;
  }
  flushDebounce(false); // clear without showing
}

// --- Debounced card notification ---

function flushDebounce(show = true) {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (show && pendingChange) {
    showChangeNotification(pendingChange);
  }
  pendingChange = null;
  pendingCardKey = null;
}

function resetDebounceTimer() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => flushDebounce(true), DEBOUNCE_TIMEOUT);
}

/**
 * Process a trade change with debouncing.
 * High-priority changes (accepted, cancelled) show immediately.
 * Card changes are debounced per-card.
 */
function processChange(change) {
  // High-priority: show immediately
  if (change.type === 'accepted') {
    flushDebounce();
    addToast('Trade accepted by other party', {
      type: 'trade',
      duration: 5000,
    });
    return;
  }
  if (change.type === 'cancelled') {
    flushDebounce();
    addToast('Trade has been cancelled', {
      type: 'removed',
      duration: 5000,
    });
    return;
  }

  // Card changes: debounce per-card
  const key = change.card
    ? `${change.card.scryfallId}_${change.card.isFoil ? 'foil' : 'nonfoil'}`
    : null;

  if (key && key === pendingCardKey) {
    // Same card — merge and reset timer
    pendingChange = change;
    resetDebounceTimer();
  } else {
    // Different card — flush old, start new
    flushDebounce();
    pendingChange = change;
    pendingCardKey = key;
    resetDebounceTimer();
  }
}

function showChangeNotification(change) {
  const cardName = change.card?.name || 'Unknown card';

  switch (change.type) {
    case 'card_added':
      addToast(`${cardName} added`, {
        type: 'trade',
        subtitle: change.card?.setName || null,
      });
      break;
    case 'card_removed':
      addToast(`${cardName} removed`, {
        type: 'removed',
        subtitle: change.card?.setName || null,
      });
      break;
    case 'status_changed': {
      const statusNames = {
        trade: 'trade',
        considering: 'considering',
        suggested: 'suggested',
      };
      const newStatus = statusNames[change.newStatus] || change.newStatus;
      addToast(`${cardName} moved to ${newStatus}`, {
        type: change.newStatus || 'info',
        subtitle: change.card?.setName || null,
      });
      break;
    }
    case 'quantity_changed':
      addToast(`${cardName} quantity: ${change.oldQuantity} → ${change.newQuantity}`, {
        type: 'trade',
        subtitle: change.card?.setName || null,
      });
      break;
    default:
      addToast(`Trade updated`, { type: 'info' });
  }
}

/**
 * Process an array of remote trade changes (from detectTradeChanges).
 * Filters to remote-only, handles debouncing.
 */
export function processTradeChanges(changes) {
  const remoteChanges = changes.filter((c) => !c.byCurrentUser);
  for (const change of remoteChanges) {
    processChange(change);
  }
}

export const notificationState = {
  get toasts() {
    return toasts;
  },
};

export { addToast, removeToast, clearAll };
